import { DatePicker, LocalizationProvider } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  Modal,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { ArrowDownward, ArrowUpward, UnfoldMore } from "@mui/icons-material";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getPendingPayoutEntriesApi, payStockAmountApi } from "src/api/commonapi";
import CustomLoader from "src/commonComponents/CustomLoader";
import { notification } from "src/utils/messages";

const MAX_SELECTION = 50;
const ENTRY_FETCH_LIMIT = 100;

const SORT_LABELS = {
  c_s_w_id: "No",
  p_name: "Product Name",
  display_name: "Member Name/Guest Name",
  date: "Date",
  amount: "Amount"
};

// "DD-MM-YYYY" strings do not sort lexicographically, so compare them numerically.
const dateSortValue = (value) => {
  const parts = String(value || "")
    .split("-")
    .map((n) => parseInt(n, 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return 0;
  const [day, month, year] = parts;
  return year * 10000 + month * 100 + day;
};

const round2 = (n) => Math.round(n * 100) / 100;

export default function WalletPayment({
  paymentModal,
  togglePaymentModal,
  handleCityWiseWalletList,
  filterData
}) {
  const user = JSON.parse(localStorage.getItem("userData"));
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 780,
    maxWidth: "95vw",
    maxHeight: "92vh",
    overflow: "auto",
    bgcolor: "background.paper",
    boxShadow: 10,
    borderRadius: "10px",
    p: 2
  };

  const [fileimage, setfileimage] = useState();

  const [isLoading, setIsLoading] = useState(false);

  const [walletData, setWalletData] = useState({
    date: new Date(),
    city_id: user.city_id,
    u_id: user.u_id,
    p_user_id: user.u_type == 2 ? user.p_user_id : user.u_id,
    name: user.name,
    note: ""
  });
  const [loader, setLoader] = useState(false);

  const [pendingEntries, setPendingEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [totalPendingAmount, setTotalPendingAmount] = useState(0);
  const [totalPendingCount, setTotalPendingCount] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [payingAmount, setPayingAmount] = useState(0);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("date");
  const [sortAsc, setSortAsc] = useState(true);

  const resetSelectionAndSummary = () => {
    setPendingEntries([]);
    setTotalPendingAmount(0);
    setTotalPendingCount(0);
    setSelectedIds([]);
    setPayingAmount(0);
  };

  const fetchPendingEntries = useCallback(async (searchTerm) => {
    setEntriesLoading(true);
    try {
      const resp = await getPendingPayoutEntriesApi({
        page: 1,
        per_page: ENTRY_FETCH_LIMIT,
        search: searchTerm || ""
      });
      if (resp?.status === 1) {
        setPendingEntries(resp?.data?.data || []);
        setTotalPendingAmount(parseFloat(resp?.data?.total_pending_amount) || 0);
        setTotalPendingCount(resp?.data?.counter || 0);
        setSelectedIds([]);
        setPayingAmount(0);
      } else {
        resetSelectionAndSummary();
        const notify = notification({
          type: "error",
          message: resp?.message || "Failed to load pending payout entries"
        });
        notify();
      }
    } catch (error) {
      resetSelectionAndSummary();
      const notify = notification({
        type: "error",
        message: "Failed to load pending payout entries"
      });
      notify();
    } finally {
      setEntriesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (paymentModal) {
      setSearch("");
      setSortKey("date");
      setSortAsc(true);
      setWalletData({
        date: new Date(),
        city_id: user.city_id,
        u_id: user.u_id,
        p_user_id: user.u_type == 2 ? user.p_user_id : user.u_id,
        name: user.name,
        note: ""
      });
      setfileimage();
      fetchPendingEntries("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentModal]);

  const isSelectionFull = selectedIds.length >= MAX_SELECTION;

  // Total of the selected entries. This is the ceiling the backend enforces on
  // `amount`, and the value Paying Amount auto-fills to.
  const selectedTotal = useMemo(
    () =>
      round2(
        pendingEntries
          .filter((entry) => selectedIds.includes(entry.c_s_w_id))
          .reduce((sum, entry) => sum + (parseFloat(entry.amount) || 0), 0)
      ),
    [pendingEntries, selectedIds]
  );

  const numericPayingAmount = parseFloat(payingAmount);
  const payingAmountInvalid =
    payingAmount === "" || Number.isNaN(numericPayingAmount) || numericPayingAmount <= 0;
  const payingAmountExceedsSelection =
    !payingAmountInvalid && numericPayingAmount > selectedTotal;
  const remainingBalance = round2(
    (totalPendingAmount || 0) - (payingAmountInvalid ? 0 : numericPayingAmount)
  );

  // Selection is the single source of truth; every change re-derives Paying Amount.
  // The user may still lower it afterwards for a partial settlement.
  const applySelection = (nextIds) => {
    setSelectedIds(nextIds);
    setPayingAmount(
      round2(
        pendingEntries
          .filter((entry) => nextIds.includes(entry.c_s_w_id))
          .reduce((sum, entry) => sum + (parseFloat(entry.amount) || 0), 0)
      )
    );
  };

  const handleToggle = (entryId) => {
    if (selectedIds.includes(entryId)) {
      applySelection(selectedIds.filter((id) => id !== entryId));
      return;
    }
    if (isSelectionFull) {
      const notify = notification({
        type: "warn",
        message: `You can settle a maximum of ${MAX_SELECTION} entries in one payment`
      });
      notify();
      return;
    }
    applySelection([...selectedIds, entryId]);
  };

  const handleSelectAll = () => {
    if (selectedIds.length > 0) {
      applySelection([]);
      return;
    }
    const allIds = pendingEntries.map((entry) => entry.c_s_w_id);
    if (allIds.length > MAX_SELECTION) {
      const notify = notification({
        type: "warn",
        message: `You can settle a maximum of ${MAX_SELECTION} entries in one payment`
      });
      notify();
      applySelection(allIds.slice(0, MAX_SELECTION));
      return;
    }
    applySelection(allIds);
  };

  const handleSort = (key) => {
    if (key === sortKey) {
      setSortAsc((prev) => !prev);
    } else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  const sortedEntries = useMemo(() => {
    const factor = sortAsc ? 1 : -1;
    return [...pendingEntries].sort((a, b) => {
      if (sortKey === "date") {
        return (dateSortValue(a.date) - dateSortValue(b.date)) * factor;
      }
      if (sortKey === "amount") {
        return ((parseFloat(a.amount) || 0) - (parseFloat(b.amount) || 0)) * factor;
      }
      if (sortKey === "c_s_w_id") {
        return (a.c_s_w_id - b.c_s_w_id) * factor;
      }
      return String(a[sortKey] ?? "").localeCompare(String(b[sortKey] ?? "")) * factor;
    });
  }, [pendingEntries, sortKey, sortAsc]);

  const allVisibleSelected =
    sortedEntries.length > 0 &&
    sortedEntries.every((entry) => selectedIds.includes(entry.c_s_w_id));

  const renderSortIcon = (key) => {
    if (key !== sortKey) return <UnfoldMore fontSize="inherit" />;
    return sortAsc ? <ArrowUpward fontSize="inherit" /> : <ArrowDownward fontSize="inherit" />;
  };

  const handleSubmitWallet = async () => {
    if (selectedIds.length === 0) {
      notification({
        type: "error",
        message: "Please select at least one pending payout entry"
      })();
      return;
    }

    if (payingAmountInvalid) {
      notification({ type: "error", message: "Please provide a valid payment amount" })();
      return;
    }

    if (payingAmountExceedsSelection) {
      notification({
        type: "error",
        message: "Payment amount is greater than the selected entries total"
      })();
      return;
    }

    if (walletData.note == undefined || walletData.note == "" || walletData.p_screenshot == undefined) {
      notification({ type: "error", message: "Please Fill Required Fields" })();
      return;
    }

    setLoader(true);

    const fdata = new FormData();
    fdata.append("selected_entry_id", JSON.stringify(selectedIds));
    fdata.append(
      "date",
      walletData.date !== "DD-MM-YYYY"
        ? moment(walletData.date).format("DD-MM-YYYY")
        : walletData.date
    );
    fdata.append("amount", round2(numericPayingAmount));
    fdata.append("city_id", walletData.city_id);
    fdata.append("u_id", walletData.u_id);
    fdata.append("p_user_id", walletData.p_user_id);
    fdata.append("name", walletData.name);
    fdata.append("note", walletData.note);
    fdata.append("stock_wallet_balance", totalPendingAmount);
    fdata.append("r_w_balance", remainingBalance);
    fdata.append("p_screenshot", walletData.p_screenshot);

    setIsLoading(true);
    try {
      const resp = await payStockAmountApi(fdata);
      setIsLoading(false);
      const notify = notification({
        type: resp?.status == 0 ? "error" : "success",
        message: resp?.message
      });
      notify();
      if (resp?.status != 0) {
        setWalletData({
          date: new Date(),
          city_id: user.city_id,
          u_id: user.u_id,
          p_user_id: user.u_type == 2 ? user.p_user_id : user.u_id,
          name: user.name,
          note: ""
        });
        setSelectedIds([]);
        setPayingAmount(0);
        togglePaymentModal();
        setfileimage();
        handleCityWiseWalletList(filterData);
      }
    } catch (error) {
      setIsLoading(false);
      const notify = notification({
        type: "error",
        message: "Something went wrong please try again"
      });
      notify();
    }

    setLoader(false);
  };

  return (
    <Modal
      open={paymentModal}
      onClose={() => togglePaymentModal()}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <Typography
          id="modal-modal-title"
          variant="h6"
          component="h2"
          style={{ marginBottom: "5px", textAlign: "center" }}
        >
          Add Payment
        </Typography>
        <hr style={{ marginBottom: 0, opacity: 0.6 }} />

        <Grid container spacing={1}>
          {/* 1. Payable Amount */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              required
              autoComplete="off"
              type="text"
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                readOnly: true
              }}
              label="Payable Amount"
              name="stock_wallet_balance"
              value={totalPendingAmount || 0}
              helperText="Total pending amount as per your wallet"
            />
          </Grid>

          {/* 2. Entry Selection */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
              Select Pending Payout Entries <span style={{ color: "red" }}>*</span>
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 1 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search by product name or guest name"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") fetchPendingEntries(search);
                }}
              />
              <Button
                variant="contained"
                onClick={() => fetchPendingEntries(search)}
                disabled={entriesLoading}
              >
                Search
              </Button>
              <Button
                variant="contained"
                color="inherit"
                onClick={() => {
                  setSearch("");
                  fetchPendingEntries("");
                }}
                disabled={entriesLoading}
              >
                Clear
              </Button>
            </Stack>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1}
              sx={{ mb: 0.5 }}
            >
              <Typography variant="body2" color="text.secondary">
                {totalPendingCount} pending entr{totalPendingCount === 1 ? "y" : "ies"}
                {totalPendingCount > sortedEntries.length
                  ? ` (showing ${sortedEntries.length})`
                  : ""}
              </Typography>
              <Chip
                label={`${selectedIds.length} of max ${MAX_SELECTION} selected`}
                size="small"
                sx={{ fontWeight: 600, fontSize: "0.7rem", height: 24 }}
              />
            </Stack>

            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                overflow: "hidden"
              }}
            >
              {entriesLoading && sortedEntries.length === 0 ? (
                <Stack alignItems="center" justifyContent="center" sx={{ py: 5 }}>
                  <CircularProgress size={26} />
                </Stack>
              ) : sortedEntries.length === 0 ? (
                <Stack alignItems="center" justifyContent="center" sx={{ py: 5, px: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    There are no pending payout entries to settle
                  </Typography>
                </Stack>
              ) : (
                <TableContainer sx={{ maxHeight: 240 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={allVisibleSelected}
                            indeterminate={selectedIds.length > 0 && !allVisibleSelected}
                            onChange={handleSelectAll}
                            inputProps={{ "aria-label": "Select all pending entries" }}
                          />
                        </TableCell>
                        {["c_s_w_id", "p_name", "display_name", "date", "amount"].map((key) => (
                          <TableCell
                            key={key}
                            sortDirection={key === sortKey ? (sortAsc ? "asc" : "desc") : false}
                            sx={{ cursor: "pointer", userSelect: "none", fontWeight: 600 }}
                            onClick={() => handleSort(key)}
                          >
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <span>{SORT_LABELS[key]}</span>
                              {renderSortIcon(key)}
                            </Stack>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedEntries.map((entry) => {
                        const isSelected = selectedIds.includes(entry.c_s_w_id);
                        const isDisabled = isSelectionFull && !isSelected;
                        return (
                          <TableRow
                            key={entry.c_s_w_id}
                            hover
                            sx={{
                              backgroundColor: isSelected
                                ? "rgba(0, 88, 190, 0.06)"
                                : "inherit",
                              opacity: isDisabled ? 0.45 : 1
                            }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                size="small"
                                checked={isSelected}
                                disabled={isDisabled}
                                onChange={() => handleToggle(entry.c_s_w_id)}
                                inputProps={{
                                  "aria-label": `Select payout entry ${entry.c_s_w_id}`
                                }}
                              />
                            </TableCell>
                            <TableCell>{entry.c_s_w_id}</TableCell>
                            <TableCell>{entry.p_name}</TableCell>
                            <TableCell>
                              <Tooltip title={entry.display_name || ""}>
                                <span>{entry.display_name || "-"}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell>{entry.date}</TableCell>
                            <TableCell align="right">{entry.amount}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
            {entriesLoading && sortedEntries.length > 0 && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 0.5 }}>
                <CustomLoader />
              </Box>
            )}
            {isSelectionFull && (
              <Typography
                variant="caption"
                color="warning.main"
                sx={{ display: "block", mt: 0.5 }}
              >
                You can settle a maximum of {MAX_SELECTION} entries in one payment
              </Typography>
            )}
          </Grid>

          {/* 3. Paying Amount */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              autoComplete="off"
              type="number"
              label="Paying Amount"
              name="amount"
              error={payingAmountExceedsSelection}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>
              }}
              value={payingAmount}
              onChange={(e) =>
                setPayingAmount(e.target.value !== "" ? e.target.value : "")
              }
              helperText={
                payingAmountExceedsSelection
                  ? "Cannot be greater than the selected entries total"
                  : selectedIds.length > 0
                  ? `Max ₹ ${selectedTotal} for ${selectedIds.length} selected entr${
                      selectedIds.length === 1 ? "y" : "ies"
                    }`
                  : "Select entries to settle"
              }
            />
          </Grid>

          {/* 4. Remaining Wallet Balance */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              autoComplete="off"
              type="text"
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                readOnly: true
              }}
              label="Remaining Wallet Balance"
              name="r_w_balance"
              value={remainingBalance}
            />
          </Grid>

          <Grid item xs={12}>
            <Stack spacing={3} style={{ marginBottom: "15px", marginTop: "15px" }}>
              {/* <label style={{ marginBottom: '10px' }}>Join Date</label> */}
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                isDisabled
                style={{ marginTop: "15px" }}
              >
                <DatePicker
                  type="date"
                  required
                  name="date"
                  label="Date"
                  value={walletData.date ? moment(walletData.date).toDate() : new Date()}
                  inputFormat="dd/MM/yyyy"
                  onChange={(newDate) => {
                    setWalletData({
                      ...walletData,
                      date: moment(newDate).format("YYYY-MM-DD")
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      sx={{
                        "& .MuiInputBase-root": {
                          height: 40
                        },
                        "& .MuiInputLabel-root": {
                          top: "-6px" // adjust this to move label vertically
                        }
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              autoComplete="off"
              type="text"
              label="Payment Note"
              required
              name="note"
              value={walletData?.note}
              onChange={(e) => {
                setWalletData({ ...walletData, note: e.target.value });
              }}
            />
          </Grid>

          <Grid item xs={12}>
            {fileimage != undefined && (
              <img
                src={fileimage}
                alt="img"
                width="100px"
                height="80px"
                style={{ margin: "5px" }}
              />
            )}
            <Button variant="contained" component="label">
              Upload Payment ScreenShot
              <input
                id="file"
                name="p_screenshot"
                type="file"
                hidden
                onChange={async (event) => {
                  setWalletData({
                    ...walletData,
                    p_screenshot: event.currentTarget.files[0]
                  });
                  setfileimage(URL.createObjectURL(event.currentTarget.files[0]));
                }}
                className="form-control"
              />
            </Button>
          </Grid>
          <Grid
            item
            xs={12}
            style={{ display: "flex", justifyContent: "end", alignItems: "center" }}
          >
            <Button
              variant="outlined"
              onClick={() => togglePaymentModal()}
              style={{ marginTop: "15px", marginRight: "15px" }}
            >
              Close
            </Button>
            <Button
              variant="contained"
              style={{ marginTop: "15px" }}
              onClick={() => !loader && handleSubmitWallet()}
              disabled={isLoading || entriesLoading}
              startIcon={isLoading && <CircularProgress size={20} color="inherit" />}
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
}
